import { autocompleteClasses, type AutocompleteRenderInputParams } from "@mui/material/Autocomplete";
import { Fragment, useCallback, useMemo } from "react";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import usePreferences from "hooks/usePreferences";
import datetime from "config/datetime";
import type { MeasurementSet } from "types";
import type { LoadVelocityProfile } from "types/load-velocity-profile";
import ListSubheader from "@mui/material/ListSubheader";
import type { CheckboxProps, TextFieldProps } from "@mui/material";
import { styled } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import { Trans, useTranslation } from "react-i18next";
import WeightHelpers from "helpers/WeightHelpers";
import LoadVelocityProfileHelpers from "helpers/LoadVelocityProfileHelpers";
import VirtualAutocomplete, { type VirtualAutocompleteProps } from "components/VirtualAutocomplete";
import ThemeProvider2024 from "components/MuiThemeProvider/ThemeProvider2024";
import Div from "components/primitives/Div";
import Checkbox from "../Checkbox";
import * as Components from "./SetSelector.components";
import Span from "components/primitives/Span";
import type { Status } from "types/Status";
import OutlinedTextField from "../OutlinedTextField";

const Alert = styled(MuiAlert)(({ theme }) => ({
  backgroundColor: theme.palette.grey[20],
  color: theme.palette.primary.main,
  fontSize: "12px",
  lineHeight: "18px",
  padding: "10px",
  "& .MuiAlert-message": {
    padding: "0",
  },
}));

const ESTIMATED_ROW_HEIGHT = 40;
const LOAD_VELOCITY_PROFILE_GROUP = "@@load-velocity-profile";

export type SetSelectorOption = LoadVelocityProfile | MeasurementSet;

type AutocompleteProps<Option extends SetSelectorOption> = Omit<
  VirtualAutocompleteProps<Option, true, false, false>,
  "renderInput" | "renderOption" | "groupBy" | "getOptionLabel" | "getOptionKey"
>;

interface SetSelectorProps<Option extends SetSelectorOption> extends AutocompleteProps<Option> {
  TextFieldProps?: Partial<TextFieldProps>;
  CheckboxProps?: Partial<Omit<CheckboxProps, "checked" | "size">>;
  status?: Status;
  /** Shows the profile section with a "still being built" notice when sets are listed without a profile. */
  emptyVelocityProfileNotice?: boolean;
  /** Checks an option's box without selecting it, e.g. to mirror a pick made in another selector. */
  getOptionChecked?: (option: Option) => boolean;
}

function SetSelector<Option extends SetSelectorOption = MeasurementSet>(props: SetSelectorProps<Option>) {
  const {
    TextFieldProps,
    CheckboxProps,
    slotProps,
    status,
    emptyVelocityProfileNotice,
    getOptionChecked,
    ...autocompleteProps
  } = props;
  const [preferences] = usePreferences();

  const { t } = useTranslation();

  const getOptionKey = useCallback(
    (option: Option) =>
      LoadVelocityProfileHelpers.isLoadVelocityProfile(option)
        ? LoadVelocityProfileHelpers.getKey(option)
        : option.completedDate,
    []
  );

  const getProfileLabel = useCallback(
    (profile: LoadVelocityProfile) =>
      t("athlete_profile.load_velocity_profile_option_label", {
        date: datetime(profile.createdAt).format("ll"),
        variant: t("measures.variant_abbr", { context: profile.variant }),
        context: profile.variant ? "unilateral" : undefined,
      }),
    [t]
  );

  // Plain text, used to search the options and as hover titles; the options themselves render rich labels.
  const getOptionLabel = useCallback(
    (option: Option) => {
      if (LoadVelocityProfileHelpers.isLoadVelocityProfile(option)) {
        return getProfileLabel(option);
      }

      const weight = WeightHelpers.toReadable(option.weightValue, preferences.imperialSystemEnabled);

      return t("set_selector.set_legend", {
        reps: option.repetitionCount,
        weight,
        variant: t("measures.variant_abbr", { context: option.variant }),
        context: option.variant ? "unilateral" : undefined,
      });
    },
    [getProfileLabel, preferences.imperialSystemEnabled, t]
  );

  // Names the side in bold for a unilateral profile, e.g. "Left side: Latest profile (Mar 9, 2026)".
  const renderProfileLabel = useCallback(
    (profile: LoadVelocityProfile) => (
      <Trans
        i18nKey="athlete_profile.load_velocity_profile_option_label"
        context={profile.variant ?? undefined}
        values={{ date: datetime(profile.createdAt).format("ll") }}
        components={{ Bold: <Span sx={{ fontWeight: "fontWeightMedium" }} /> }}
      />
    ),
    []
  );

  const renderSetOption = useCallback(
    (option: Option, _index: number, { selected }: { selected: boolean }) => {
      const checked = selected || Boolean(getOptionChecked?.(option));

      if (LoadVelocityProfileHelpers.isLoadVelocityProfile(option)) {
        return (
          <ThemeProvider2024>
            <Div sx={{ display: "flex", alignItems: "center" }}>
              <Checkbox checked={checked} {...CheckboxProps} />
              <Typography component="span" variant="TextS">
                {renderProfileLabel(option)}
              </Typography>
            </Div>
          </ThemeProvider2024>
        );
      }

      return (
        <ThemeProvider2024>
          <Checkbox checked={checked} {...CheckboxProps} />
          <Typography component="span" variant="body2">
            {getOptionLabel(option)}
          </Typography>
        </ThemeProvider2024>
      );
    },
    [CheckboxProps, getOptionChecked, getOptionLabel, renderProfileLabel]
  );

  const hasVelocityProfiles = useMemo(
    () => autocompleteProps.options.some(LoadVelocityProfileHelpers.isLoadVelocityProfile),
    [autocompleteProps.options]
  );

  const groupBy = useCallback((option: Option) => {
    return LoadVelocityProfileHelpers.isLoadVelocityProfile(option)
      ? LOAD_VELOCITY_PROFILE_GROUP
      : datetime(option.completedDate).format("ddd, DD MMM YY");
  }, []);

  // Sets without profiles still get a profile section, explaining why there is nothing to pick yet.
  const showEmptyVelocityProfile =
    Boolean(emptyVelocityProfileNotice) && !hasVelocityProfiles && status !== "fetching" && status !== "error";

  // "All sets" separates sets from the profile section, so it heads the first set group whenever that section shows.
  const firstSetGroup = useMemo(() => {
    if (!hasVelocityProfiles && !showEmptyVelocityProfile) return;
    const firstSet = autocompleteProps.options.find(
      (option) => !LoadVelocityProfileHelpers.isLoadVelocityProfile(option)
    );
    return firstSet && groupBy(firstSet);
  }, [autocompleteProps.options, groupBy, hasVelocityProfiles, showEmptyVelocityProfile]);

  const renderGroupLabel = useCallback(
    (group: string) => {
      if (group === LOAD_VELOCITY_PROFILE_GROUP) {
        return (
          <ThemeProvider2024>
            <ListSubheader
              component="div"
              sx={{ display: "flex", flexDirection: "column", gap: "4px", padding: "16px 0 4px 0" }}
            >
              <Typography sx={{ fontWeight: "fontWeightMedium", color: "grey.160" }}>
                {t("global.load_velocity_profile_label")}
              </Typography>
              <Typography variant="TextXS" sx={{ color: "grey.120", fontWeight: "fontWeightRegular" }}>
                {t("athlete_profile.load_velocity_profile_footnote")}
              </Typography>
            </ListSubheader>
          </ThemeProvider2024>
        );
      }

      return (
        <ThemeProvider2024>
          <ListSubheader component="div" sx={{ padding: 0 }}>
            {group === firstSetGroup && showEmptyVelocityProfile && (
              <Div sx={{ display: "flex", flexDirection: "column", gap: "4px", padding: "16px 0 4px 0" }}>
                <Typography sx={{ fontWeight: "fontWeightMedium", color: "grey.160", opacity: 0.5 }}>
                  {t("global.load_velocity_profile_label")}
                </Typography>
                <Typography variant="TextXS" sx={{ color: "grey.120", fontWeight: "fontWeightRegular", opacity: 0.5 }}>
                  {t("athlete_profile.load_velocity_profile_footnote")}
                </Typography>
                <Alert icon={false}>{t("athlete_profile.load_velocity_profile_empty")}</Alert>
              </Div>
            )}
            {group === firstSetGroup && (
              <Fragment>
                <Divider sx={{ margin: "10px 0 16px" }} />
                <Typography sx={{ fontWeight: "fontWeightMedium", color: "grey.160", mb: "6px" }}>
                  {t("global.all_sets_label")}
                </Typography>
              </Fragment>
            )}
            <Typography variant="TextXS" sx={{ color: "grey.100" }}>
              {group}
            </Typography>
          </ListSubheader>
        </ThemeProvider2024>
      );
    },
    [firstSetGroup, showEmptyVelocityProfile, t]
  );

  const renderAutocompleteInput = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <OutlinedTextField
        variant="outlined"
        {...params}
        fullWidth
        label={t("set_selector.sets_input_label")}
        size="small"
        color="primary"
        sx={{
          [`& .${autocompleteClasses.inputRoot}`]: {
            flexWrap: "nowrap",
          },
        }}
        {...TextFieldProps}
        slotProps={{
          input: {
            ...params.InputProps,
            ...TextFieldProps?.slotProps?.input,
            endAdornment: (
              <Fragment>
                {status === "fetching" ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </Fragment>
            ),
          },
        }}
      />
    ),
    [TextFieldProps, t, status]
  );

  const renderInputValue = useCallback(
    (options: Option[]) => {
      const profile = options.find((option) => LoadVelocityProfileHelpers.isLoadVelocityProfile(option));

      // A selected profile excludes sets, so it names the selection instead of a set count.
      if (profile) {
        return (
          <Components.ProfileInputValue title={getProfileLabel(profile)}>
            {renderProfileLabel(profile)}
          </Components.ProfileInputValue>
        );
      }

      return <Div sx={{ padding: "0 8px" }}>{t("workouts.exercise_input_value_set", { count: options.length })}</Div>;
    },
    [getProfileLabel, renderProfileLabel, t]
  );

  const mergedSlotProps = useMemo(
    () =>
      ({
        popper: { placement: "bottom-start", style: { width: 360 } },
        listbox: {
          sx: {
            [`& .${autocompleteClasses.groupLabel}`]: { paddingX: 2 }, // the wrapper
            [`& .${autocompleteClasses.option}`]: { paddingX: 2 }, // the options
          },
        },
        ...slotProps,
      }) as const,
    [slotProps]
  );

  return (
    <ThemeProvider2024>
      <VirtualAutocomplete<Option, true>
        multiple
        fullWidth
        disableCloseOnSelect
        getOptionLabel={getOptionLabel}
        getOptionKey={getOptionKey}
        groupBy={groupBy}
        estimateItemHeight={ESTIMATED_ROW_HEIGHT}
        renderOptionContent={renderSetOption}
        renderGroupLabel={renderGroupLabel}
        {...autocompleteProps}
        slotProps={mergedSlotProps}
        renderValue={renderInputValue}
        renderInput={renderAutocompleteInput}
      />
    </ThemeProvider2024>
  );
}

export default SetSelector;
